import { supabase } from '../lib/supabaseClient'

/**
 * First-party visitor analytics.
 *
 * Writes to the existing Supabase tables `visitor_sessions`, `visitor_events`
 * and `traffic_sources` using ONLY the public anon client. This runs alongside
 * GA4 (utils/analytics.ts) — it never replaces it and never mirrors GA4 data.
 *
 * Every write is fire-and-forget and fully guarded: analytics failures are
 * logged with console.warn and can never break a product flow.
 */

const VISITOR_ID_KEY = 'toxicdetector_visitor_id'
const SESSION_FLAG_KEY = 'toxicdetector_session_started'
const GEO_CACHE_KEY = 'toxicdetector_geo'
const ONCE_PREFIX = 'toxicdetector_tracked:'

export type EventMetadata = Record<string, unknown>

interface GeoContext {
  country: string | null
  city: string | null
  ip_hash: string | null
}

interface DeviceContext {
  device: string
  browser: string
  os: string
}

const EMPTY_GEO: GeoContext = {
  country: null,
  city: null,
  ip_hash: null,
}

function warn(error: unknown) {
  console.warn('[Analytics]', error)
}

const isBrowser = () => typeof window !== 'undefined'

function safeLocalGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeLocalSet(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    /* storage unavailable — tracking degrades, app continues */
  }
}

function safeSessionGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSessionSet(key: string, value: string) {
  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    /* storage unavailable — tracking degrades, app continues */
  }
}

function createUuid(): string {
  const cryptoRef = typeof crypto !== 'undefined' ? crypto : undefined
  if (cryptoRef && typeof cryptoRef.randomUUID === 'function') {
    return cryptoRef.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0
    const value = char === 'x' ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

/**
 * Persistent anonymous visitor id. Deliberately NOT the Supabase user id —
 * a visitor exists before, across and after any authenticated session.
 */
export function getVisitorId(): string {
  if (!isBrowser()) return 'server'
  const existing = safeLocalGet(VISITOR_ID_KEY)
  if (existing) return existing
  const generated = createUuid()
  safeLocalSet(VISITOR_ID_KEY, generated)
  return generated
}

/* ------------------------------------------------------------------ */
/* User association                                                     */
/* ------------------------------------------------------------------ */

let currentUserId: string | null = null

/** Called by AuthContext whenever the authenticated user changes. */
export function setAnalyticsUser(userId: string | null) {
  currentUserId = userId ?? null
}

async function resolveUserId(): Promise<string | null> {
  if (currentUserId) return currentUserId
  try {
    const { data } = await supabase.auth.getSession()
    currentUserId = data.session?.user?.id ?? null
  } catch (error) {
    warn(error)
  }
  return currentUserId
}

/* ------------------------------------------------------------------ */
/* Device / browser / OS                                                */
/* ------------------------------------------------------------------ */

function getDeviceContext(): DeviceContext {
  if (!isBrowser()) {
    return {
      device: 'unknown',
      browser: 'unknown',
      os: 'unknown',
    }
  }
  const ua = window.navigator.userAgent || ''
  const isTablet = /iPad|Tablet|PlayBook|Silk/i.test(ua)
  const isMobile = !isTablet && /Mobi|Android|iPhone|iPod|Windows Phone/i.test(ua)
  let browser = 'other'
  if (/Edg\//i.test(ua)) browser = 'edge'
  else if (/OPR\/|Opera/i.test(ua)) browser = 'opera'
  else if (/Chrome\//i.test(ua)) browser = 'chrome'
  else if (/Firefox\//i.test(ua)) browser = 'firefox'
  else if (/Safari\//i.test(ua)) browser = 'safari'
  let os = 'other'
  if (/Windows/i.test(ua)) os = 'windows'
  else if (/Android/i.test(ua)) os = 'android'
  else if (/iPhone|iPad|iPod/i.test(ua)) os = 'ios'
  else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macos'
  else if (/Linux/i.test(ua)) os = 'linux'
  return {
    device: isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop',
    browser,
    os,
  }
}

/* ------------------------------------------------------------------ */
/* Geo (best effort, cached for the browser session)                    */
/* ------------------------------------------------------------------ */

async function sha256(value: string): Promise<string | null> {
  try {
    if (typeof crypto === 'undefined' || !crypto.subtle) return null
    const bytes = new TextEncoder().encode(value)
    const digest = await crypto.subtle.digest('SHA-256', bytes)
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
  } catch {
    return null
  }
}

let geoPromise: Promise<GeoContext> | null = null

function getGeoSync(): GeoContext {
  const cached = safeSessionGet(GEO_CACHE_KEY)
  if (!cached) return EMPTY_GEO
  try {
    return JSON.parse(cached) as GeoContext
  } catch {
    return EMPTY_GEO
  }
}

async function getGeo(): Promise<GeoContext> {
  if (!isBrowser()) return EMPTY_GEO
  const cached = safeSessionGet(GEO_CACHE_KEY)
  if (cached) return getGeoSync()
  if (geoPromise) return geoPromise
  geoPromise = (async () => {
    try {
      const controller = new AbortController()
      const timeout = window.setTimeout(() => controller.abort(), 3000)
      const response = await fetch('https://ipapi.co/json/', {
        signal: controller.signal,
      })
      window.clearTimeout(timeout)
      if (!response.ok) throw new Error(`Geo lookup failed: ${response.status}`)
      const data = (await response.json()) as {
        ip?: string
        city?: string
        country_name?: string
        country_code?: string
      }
      const geo: GeoContext = {
        country: data.country_name || data.country_code || null,
        city: data.city || null,
        ip_hash: data.ip ? await sha256(data.ip) : null,
      }
      safeSessionSet(GEO_CACHE_KEY, JSON.stringify(geo))
      return geo
    } catch (error) {
      warn(error)
      safeSessionSet(GEO_CACHE_KEY, JSON.stringify(EMPTY_GEO))
      return EMPTY_GEO
    }
  })()
  return geoPromise
}

/* ------------------------------------------------------------------ */
/* Traffic source normalization                                         */
/* ------------------------------------------------------------------ */

const REFERRER_MAP: Array<[RegExp, string]> = [
  [/linkedin\./i, 'linkedin'],
  [/tiktok\./i, 'tiktok'],
  [/(youtube\.|youtu\.be)/i, 'youtube'],
  [/google\./i, 'google'],
  [/bing\./i, 'bing'],
  [/duckduckgo\./i, 'duckduckgo'],
  [/yahoo\./i, 'yahoo'],
  [/reddit\./i, 'reddit'],
  [/(facebook\.|fb\.com)/i, 'facebook'],
  [/instagram\./i, 'instagram'],
  [/(twitter\.com|^x\.com|\.x\.com)/i, 'twitter'],
  [/pinterest\./i, 'pinterest'],
  [/quora\./i, 'quora'],
  [/(t\.co)/i, 'twitter'],
  [/(whatsapp|wa\.me)/i, 'whatsapp'],
  [/(telegram|t\.me)/i, 'telegram'],
  [/threads\./i, 'threads'],
  [/snapchat\./i, 'snapchat'],
]

export function classifyTrafficSource(
  referrer: string | null,
  utmSource: string | null,
): string {
  if (utmSource) return utmSource.toLowerCase()
  if (!referrer) return 'direct'
  let host = ''
  try {
    host = new URL(referrer).hostname
  } catch {
    return 'direct'
  }
  if (isBrowser() && host === window.location.hostname) return 'direct'
  const match = REFERRER_MAP.find(([pattern]) => pattern.test(host))
  return match ? match[1] : 'referral'
}

function getSearchKeyword(params: URLSearchParams, referrer: string | null) {
  const direct = params.get('utm_term') || params.get('q') || params.get('query')
  if (direct) return direct
  if (!referrer) return null
  try {
    return new URL(referrer).searchParams.get('q')
  } catch {
    return null
  }
}

/* ------------------------------------------------------------------ */
/* Session                                                              */
/* ------------------------------------------------------------------ */

let sessionPromise: Promise<void> | null = null

/**
 * Inserts exactly one `visitor_sessions` row per browser session.
 * Guarded by both an in-memory promise (React Strict Mode double effects)
 * and sessionStorage (remounts, hard navigations within the tab).
 */
export function ensureVisitorSession(): Promise<void> {
  if (!isBrowser()) return Promise.resolve()
  if (sessionPromise) return sessionPromise
  if (safeSessionGet(SESSION_FLAG_KEY)) {
    // Session already recorded in this tab — still warm the geo cache.
    sessionPromise = getGeo().then(() => undefined)
    return sessionPromise
  }
  // Claim the session synchronously so a concurrent call cannot double-insert.
  safeSessionSet(SESSION_FLAG_KEY, new Date().toISOString())
  sessionPromise = (async () => {
    try {
      const params = new URLSearchParams(window.location.search)
      const referrer = document.referrer ? document.referrer : null
      const utmSource = params.get('utm_source')
      const [geo, userId] = await Promise.all([getGeo(), resolveUserId()])
      const { device, browser, os } = getDeviceContext()
      const trafficSource = classifyTrafficSource(referrer, utmSource)
      const landingPage = window.location.pathname
      const visitorId = getVisitorId()
      const createdAt = new Date().toISOString()
      const { error } = await supabase.from('visitor_sessions').insert({
        visitor_id: visitorId,
        user_id: userId,
        country: geo.country,
        city: geo.city,
        device,
        browser,
        os,
        referrer,
        traffic_source: trafficSource,
        utm_source: utmSource,
        utm_medium: params.get('utm_medium'),
        utm_campaign: params.get('utm_campaign'),
        landing_page: landingPage,
        search_keyword: getSearchKeyword(params, referrer),
        ip_hash: geo.ip_hash,
        created_at: createdAt,
      })
      if (error) warn(error)
      await recordTrafficSource({
        visitorId,
        source: trafficSource,
        medium: params.get('utm_medium'),
        campaign: params.get('utm_campaign'),
        referrer,
        landingPage,
        createdAt,
      })
    } catch (error) {
      warn(error)
    }
  })()
  return sessionPromise
}

async function recordTrafficSource(input: {
  visitorId: string
  source: string
  medium: string | null
  campaign: string | null
  referrer: string | null
  landingPage: string
  createdAt: string
}) {
  try {
    const { error } = await supabase.from('traffic_sources').insert({
      visitor_id: input.visitorId,
      source: input.source,
      medium: input.medium,
      campaign: input.campaign,
      referrer: input.referrer,
      landing_page: input.landingPage,
      created_at: input.createdAt,
    })
    if (error) warn(error)
  } catch (error) {
    warn(error)
  }
}

/* ------------------------------------------------------------------ */
/* Events                                                               */
/* ------------------------------------------------------------------ */

/**
 * Records a single row in `visitor_events`. Always safe to call — resolves
 * even when the write fails, so callers never need a try/catch.
 */
export async function trackEvent(
  eventType: string,
  metadata?: EventMetadata,
): Promise<void> {
  if (!isBrowser()) return
  try {
    const userId = await resolveUserId()
    const geo = getGeoSync()
    const { device, browser } = getDeviceContext()
    const { error } = await supabase.from('visitor_events').insert({
      visitor_id: getVisitorId(),
      user_id: userId,
      event_type: eventType,
      page: window.location.pathname,
      country: geo.country,
      device,
      browser,
      referrer: document.referrer ? document.referrer : null,
      metadata: metadata ?? {},
      created_at: new Date().toISOString(),
    })
    if (error) warn(error)
  } catch (error) {
    warn(error)
  }
}

const firedKeys = new Set<string>()

/**
 * Fires an event at most once for the given dedupe key. Guards both in memory
 * (Strict Mode / remounts) and in localStorage (reloads, back navigation) so
 * milestone events like signup_completed or purchase_completed cannot double.
 */
export async function trackEventOnce(
  dedupeKey: string,
  eventType: string,
  metadata?: EventMetadata,
): Promise<void> {
  if (!isBrowser()) return
  const key = `${ONCE_PREFIX}${dedupeKey}`
  if (firedKeys.has(key)) return
  firedKeys.add(key)
  if (safeLocalGet(key)) return
  safeLocalSet(key, '1')
  await trackEvent(eventType, metadata)
}

/** Standardized CTA click event. */
export function trackCtaClick(
  cta: string,
  location: string,
  destination?: string,
): void {
  void trackEvent('cta_clicked', {
    cta,
    location,
    destination: destination ?? null,
  })
}
